import { useState, type ChangeEvent } from "react";
import moment from "moment/moment";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
import Button from "../UI/Button";
import Text from "../UI/Text";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import DocumentDownloader from "../UI/DocumentDownloader";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { useFichierVirement } from "../../hooks/use-compta-pro";
import { euros } from "./comptaUtils";
import styles from "./Compta.module.css";

export interface VirementLigne {
  idProfileBailleur: number;
  nom: string;
  solde: number;
  ibanManquant: boolean;
}

interface FichierVirementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lignes: VirementLigne[];
  /** Appelé une fois le fichier généré, par exemple pour vider la sélection. */
  onGenerated?: () => void;
}

function today(): string {
  return moment().format("YYYY-MM-DD");
}

export default function FichierVirementModal({
  open,
  onOpenChange,
  lignes,
  onGenerated,
}: FichierVirementModalProps) {
  const toast = useToast();
  const { genererFichierVirement } = useFichierVirement();
  const [dateExecution, setDateExecution] = useState(today);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const virementsValides = lignes.filter(
    (ligne) => !ligne.ibanManquant && ligne.solde > 0,
  );
  const total = virementsValides.reduce(
    (somme, ligne) => somme + ligne.solde,
    0,
  );

  function close() {
    setDateExecution(today());
    onOpenChange(false);
  }

  async function submit(setSignedUrl: (url: string) => void) {
    setIsSubmitting(true);
    try {
      const summary = await genererFichierVirement(
        lignes.map((ligne) => ligne.idProfileBailleur),
        dateExecution,
      );
      setSignedUrl(summary.url);
      toast.success({
        title: `Fichier de virement généré — ${euros(summary.total)}`,
        description:
          summary.skipped.length > 0
            ? `${
                summary.virements.length
              } virement(s). Ignorés : ${summary.skipped.join(" ; ")}`
            : `${summary.virements.length} virement(s). Déposez le fichier XML sur votre espace bancaire.`,
      });
      onGenerated?.();
      close();
    } catch (error) {
      toast.error({
        title: "Génération impossible",
        description: handleAPIError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <ModalDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) close();
      }}
      header="Fichier de virement des reversements"
      size="md"
    >
      <ModalDialogBody className={styles.modalForm}>
        <Text>
          Génère un fichier SEPA (pain.001) à déposer sur votre espace bancaire
          : chaque mandant sélectionné reçoit son solde dû sur l'IBAN de son
          profil, depuis votre compte de gestion (IBAN/BIC du profil
          mandataire).
        </Text>
        <Table>
          <Table.Header>
            <Table.Column>Mandant</Table.Column>
            <Table.Column align="right">Solde à reverser</Table.Column>
          </Table.Header>
          <Table.Body>
            {lignes.map((ligne) => (
              <Table.Row key={ligne.idProfileBailleur}>
                <Table.Cell>{ligne.nom}</Table.Cell>
                <Table.Cell align="right">
                  {ligne.ibanManquant ? (
                    <Status
                      color="warning"
                      text="IBAN ou BIC manquant sur le profil"
                    />
                  ) : ligne.solde <= 0 ? (
                    <Status
                      color="neutral"
                      text={`Rien à reverser (${euros(ligne.solde)})`}
                    />
                  ) : (
                    euros(ligne.solde)
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.Cell>
                <Text as="span" weight="bold">
                  Total ({virementsValides.length} virement(s))
                </Text>
              </Table.Cell>
              <Table.Cell align="right">
                <Text as="span" weight="bold">
                  {euros(total)}
                </Text>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
        {virementsValides.length < lignes.length && (
          <Callout variant="warning">
            Les mandants signalés ne seront pas inclus dans le fichier :
            complétez l'IBAN et le BIC dans la fiche du profil concerné.
          </Callout>
        )}
        <TextInputStandalone
          label="Date d'exécution des virements"
          type="date"
          value={dateExecution}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setDateExecution(e.target.value)
          }
        />
      </ModalDialogBody>
      <DocumentDownloader>
        {(setSignedUrl: (url: string) => void) => (
          <ModalDialogFooter>
            <Button variant="secondary" onClick={close}>
              Annuler
            </Button>
            <Button
              onClick={() => submit(setSignedUrl)}
              disabled={virementsValides.length === 0 || !dateExecution}
              isLoading={isSubmitting}
            >
              Générer le fichier
            </Button>
          </ModalDialogFooter>
        )}
      </DocumentDownloader>
    </ModalDialog>
  );
}
